import {useContext, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';

import {NavLink, useNavigate} from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography
} from '@mui/material';

import { styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import {LOGIN, PREFERENCES} from "../../../../routes";
import useLogout from "../../../../hooks/useLogout";
import UserContext from "../../../../hooks/UserContext";
import LanguageSelector from '../../../../components/LanguageSelector';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)}
`
);

function HeaderUserbox() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const {logoutUser} = useLogout()
  const navigate = useNavigate()
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  return (
    <>
      {user?.roles?.includes("public") && <><UserBoxButton color="secondary" onClick={() => navigate(LOGIN)}><Avatar variant="rounded" /></UserBoxButton><LanguageSelector /></>}
      {!user?.roles?.includes("public") && (<>
        <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
          <Avatar variant="rounded" alt={user?.firstname} src={user?.gravatar} />
          <Hidden mdDown>
            <UserBoxText>
              <UserBoxLabel variant="body1">{user?.firstname}&nbsp;{user?.lastname}</UserBoxLabel>
              <UserBoxDescription variant="body2">
                {user?.email}
              </UserBoxDescription>
            </UserBoxText>
          </Hidden>
          <Hidden smDown>
            <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
          </Hidden>
        </UserBoxButton>
        <LanguageSelector />
      </>)}
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <Avatar variant="rounded" alt={user?.firstname} src={user?.gravatar} />
          <UserBoxText>
            <UserBoxLabel variant="body1">{user?.firstname}&nbsp;{user?.lastname}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {user?.email}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <List sx={{ p: 1 }} component="nav">
          <ListItem
            button
            to={PREFERENCES}
            component={NavLink}
          >
            <AccountTreeTwoToneIcon fontSize="small" />
            <ListItemText primary={t('navigation.myPreferences')} />
          </ListItem>
        </List>
        <Divider />
        <Box sx={{ p: 1 }}>
          <List sx={{ p: 1 }} component="nav">
            <ListItem
              button
            >
              <LockOpenTwoToneIcon fontSize="small" />
              <ListItemText
                onClick={() => {logoutUser()}}
                primary={t('navigation.logout')} />
            </ListItem>
          </List>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
